import React, { useContext, useEffect, useState } from 'react';
import { AuthContext } from '../contexts/auth';
import Swal from 'sweetalert2';
import { getEmployeeContractsList } from '../services/api_contracts_employee';
import { getWeeklyWorkingHourCompound, createEmployeeWeeklyWorkingHour, getEmployeeWeeklyWorkingHour, updateEmployeeWeeklyWorkingHour, deleteEmployeeWeeklyWorkingHour } from '../services/api_working_hours';
import ComboboxAutocomplete from './combobox_autocomplete';
import { BriefcaseIcon, EyeSlashIcon } from '@heroicons/react/24/outline';
import { Link, useParams } from 'wouter';
import LocalBarOutlinedIcon from '@mui/icons-material/LocalBarOutlined';
import Fetching from './fetching';
import WeeklyWorkingHoursCompoundSelect from './weekly_wh_compound_select';



const TurnoTabEmployee = ({ employeeId, setUnsavedChanges, employee, employeeEmailAuth, onTabContractChange }) => {
    const { token, profile } = useContext(AuthContext);
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState(null);
    const daysOfWeek = ['Lunedì', 'Martedì', 'Mercoledì', 'Giovedì', 'Venerdì', 'Sabato', 'Domenica']; 
    const [contracts, setContracts] = useState([]);
    const [employeeContract, setEmployeeContract] = useState(null);
    const [shiftType, setShiftType] = useState('');
    const [weeklyWorkingHours, setWeeklyWorkingHours] = useState([]);
    const [selectedWorkingHour, setSelectedWorkingHour] = useState(null);
    const [wwhEmployee, setWwhEmployee] = useState(null);
    const [isWwhLoading, setIsWwhLoading] = useState(false);
    const { codiceFiscale } = useParams();  

    const wwhEmployeeData = weeklyWorkingHours.find(wwh => wwh.id === wwhEmployee);
    
    const hasManageShiftsPermission = profile?.customer_type === 'employee' && (
        profile?.employee?.permissions.includes('hr.manage_shifts_all') || 
        profile?.employee?.permissions.includes('hr.manage_shifts_ou')
    );
    
    useEffect(() => {
        if (!employeeId) {
            return;
        }
        getContractList();
    }, [employeeId]);

    const getContractList = () => {
        setIsLoading(true);
        setError(null);

        getEmployeeContractsList(token, employeeId, 'payrolls')
            .then(data => {
                setContracts(data);

                const activeContract = data.find(contract => contract.is_active);
                if (activeContract) {
                    setEmployeeContract(activeContract);
                    setShiftType(activeContract.shift_type || '');
                } else {
                    setEmployeeContract(null);
                    setShiftType('');
                }
            })
            .catch(error => {
                setError(error);
            })
            .finally(() => {
                setIsLoading(false);
            });
    };

    useEffect(() => {
        if (!token || !employeeId) return;

        const fetchWeekWorkingHours = async () => {
            setIsWwhLoading(true);
            try {
                const res = await getWeeklyWorkingHourCompound(token, '', employeeId);
                setWeeklyWorkingHours(res || []);
            } catch (error) {
                console.error('Errore durante il recupero del turno', error);
                setWeeklyWorkingHours([]);
            } finally {
                setIsWwhLoading(false);
            }
        };

        fetchWeekWorkingHours();
    }, [token, employeeId]);

    useEffect(() => {
        const fetchEmployeeWeeklyWorkingHourData = async () => {
          try {
           
            const employeeWeeklyWorkingHourData = await getEmployeeWeeklyWorkingHour(token, employeeId);
            if (employeeWeeklyWorkingHourData && employeeWeeklyWorkingHourData.weekly_working_hour) {
                setWwhEmployee(employeeWeeklyWorkingHourData.weekly_working_hour);
              }
  
          } catch (error) {
            console.error('Errore durante il recupero dell\'orario lavorativo', error);
          }
        };
      
        fetchEmployeeWeeklyWorkingHourData();
    }, [token, employeeId]);
      

    const handleSelectWorkingHour = (selected) => {
        setSelectedWorkingHour(selected);
        setUnsavedChanges(true);
    };

    const handleSaveEmployeeWWH = async () => {
        if (!selectedWorkingHour) return;
    
        let weekHoursEmployee = employeeContract.week_hours * 60;
    
        const saveAndNotify = async () => {
            try {
                await saveWeeklyWorkingHour();
                setUnsavedChanges(false);
                
                Swal.fire({
                    title: 'Turno settimanale assegnato con successo!',
                    icon: 'success',
                    showConfirmButton: false,
                    timer: 1500,
                });
    
            } catch (error) {
                Swal.fire({
                    icon: 'error',
                    title: 'Errore',
                    text: error.message || 'Errore sconosciuto',
                });
            }
        };
        
        if (weekHoursEmployee !== selectedWorkingHour.duration) {
            const result = await Swal.fire({
                title: 'Confermi?',
                html: `L'orario settimanale del dipendente è di <b>${formatMinutesToHours(weekHoursEmployee)}</b> e stai assegnando un orario settimanale di <b>${formatMinutesToHours(selectedWorkingHour.duration)}</b>. Confermi l'operazione?`,
                icon: 'info',
                showCancelButton: true,
                confirmButtonText: 'Conferma',
                cancelButtonText: 'Annulla',
                cancelButtonColor: '#d1d5db',
                allowOutsideClick: false,
                allowEscapeKey: false,
            });
    
            if (!result.isConfirmed) return;
        }

        await saveAndNotify();
    };
       
    
    const saveWeeklyWorkingHour = async () => {
        const wwhId = selectedWorkingHour.id;
    
        if (typeof wwhId !== 'number') {
            throw new Error('ID del turno settimanale non valido');
        }
    
        const data = {
            weekly_working_hour: wwhId
        };
    
        const response = !wwhEmployee
            ? await createEmployeeWeeklyWorkingHour(token, employeeId, data)
            : await updateEmployeeWeeklyWorkingHour(token, employeeId, data);
    
        setSelectedWorkingHour(null);
        setWwhEmployee(wwhId);
    };    
    
    const handleCancelSave = () => {
        setSelectedWorkingHour(null);
        setUnsavedChanges(false);
    }


    const handleDeleteEmployeeWWH = async () => {
        Swal.fire({
            title: 'Sei sicuro di voler eliminare l\'assegnazione del turno?',
            icon: 'warning',
            showCancelButton: true,
            confirmButtonText: 'Conferma',
            cancelButtonText: 'Annulla',
            cancelButtonColor: '#d1d5db',
        }).then(async (result) => {
            if (result.isConfirmed) {
                try {
                    if (!wwhEmployeeData || !wwhEmployeeData.id) {
                        throw new Error('Nessun turno da eliminare');
                    }

                    const refOu = wwhEmployeeData.ou || null;
    
                    await deleteEmployeeWeeklyWorkingHour(token, employeeId);
                    setWwhEmployee(null);
    
                    await Swal.fire({
                        title: 'Turno eliminato con successo!',
                        icon: 'success',
                        timer: 1500,
                        showConfirmButton: false,
                        allowOutsideClick: false,
                        allowEscapeKey: false,
                    }); 
                } catch (error) {
                    Swal.fire({
                        title: 'Errore durante l\'eliminazione del turno settimanale',
                        text: error.message || 'Errore sconosciuto',
                        icon: 'error',
                    });
                }
            }
        });
    };  

    const formatMinutesToHours = (minutes) => {
    const totalHours = Math.floor(minutes / 60);
    const remainingMinutes = minutes % 60;

    if (totalHours === 0 && remainingMinutes === 0) {
        return '0 min';
    } else if (totalHours === 0) {
        return `${remainingMinutes} min`;
    } else if (remainingMinutes === 0) {
        return `${totalHours} ore`;
    } else {
        return `${totalHours} ore, ${remainingMinutes} min`;
    }
    };

    if (isLoading) return <Fetching />
    if (error) return <div>Error: {error.message}</div>;

    const wwhEmployeeDataDisplay = selectedWorkingHour || wwhEmployeeData;

    return (
        <div className='container px-4 w-full overflow-y-auto' style={{ minHeight: '50vh'}}>
            {employeeContract ? (
                <div className='mx-4 mb-10'>
                    {employeeContract.week_hours ? (
                        <>
                            {shiftType === 'fisso' ? (
                                <>  
                                    <div className='grid grid-cols-3 py-4'>
                                        <div className='col-span-1 flex items-center'>
                                            <div>
                                                <h3 className='block text-sm font-medium text-gray-700'>Seleziona il turno da assegnare</h3>
                                            </div>

                                        </div>
                                        {isWwhLoading ? (
                                            <div className='text-sm text-gray-600'>Caricamento...</div>
                                        ) : (
                                            <div className='col-span-2 items-center'>
                                                <WeeklyWorkingHoursCompoundSelect
                                                    token={token}
                                                    permission="payrolls"
                                                    sourceItems={weeklyWorkingHours}
                                                    value={selectedWorkingHour}
                                                    onChange={handleSelectWorkingHour}
                                                    placeholder="Cerca il turno con nome..."
                                                />
                                            </div>
                                        )}
                                    </div>

                                    {isWwhLoading ? (
                                        <Fetching />
                                    ) : (!weeklyWorkingHours || weeklyWorkingHours.length === 0) ? (
                                        <div className='col-span-5 flex justify-center py-5 items-center w-full rounded-lg border-2 border-dashed border-gray-300 text-sm font-semibold text-gray-900'>
                                            <div className="text-center p-5">
                                            <p>Nessun turno settimanale presente da assegnare</p>
                                            <p>Vai alla pagina di <Link to="/shift_management/create?type=weekly&mode=scratch"><span className='underline text-indigo-700 uppercase cursor-pointer'>creazione turno</span></Link></p>
                                            </div>
                                        </div>
                                    ) : null}

                                    {(wwhEmployeeData && !selectedWorkingHour) && (
                                        <div className='pt-6 grid grid-cols-1 sm:grid-cols-2 justify-around'>
                                            <div className='col-span-1'>
                                                <h3 className='block text-sm font-medium text-gray-700'>Turno assegnato: <span className='uppercase font-semibold text-indigo-700'>{wwhEmployeeData?.name}</span></h3>
                                            </div>
                                            <div className='col-span-1 sm:text-end text-sm font-medium text-gray-700'>
                                            Totale Ore Settimanali: {wwhEmployeeData?.duration !== undefined ? formatMinutesToHours(wwhEmployeeData.duration) : 'Non disponibile'}
                                            </div>
                                            
                                        </div>
                                    )}
                                    
                                    {wwhEmployeeDataDisplay && (
                                        <div className='mt-4 overflow-x-auto'>
                                            {(selectedWorkingHour && selectedWorkingHour.duration !== null) && (
                                                <div className='flex justify-start text-sm font-medium text-gray-700 pb-4'>
                                                    Totale Ore Settimanali: {formatMinutesToHours(selectedWorkingHour.duration)}
                                                </div>
                                            )}
                                            <table className="w-full rounded-md border-collapse overflow-auto" style={{ maxWidth: '100%' }}>
                                                <thead>
                                                    <tr>
                                                        {daysOfWeek.slice(0, 7).map((day, index) => ( 
                                                            <th key={index} className="px-4 py-2 text-sm font-bold text-center whitespace-nowrap text-gray-500 border border-gray-300 rounded-md" style={{ width: `${100 / 7}%` }}>
                                                                {day}
                                                            </th>
                                                        ))}
                                                    </tr>
                                                </thead>
                                                <tbody>
                                                    <tr className="px-1 py-2 text-sm text-center whitespace-nowrap text-gray-700">
                                                        {daysOfWeek.map((day, columnIndex) => (
                                                            <td key={columnIndex} className="px-4 py-2 text-sm text-center whitespace-nowrap text-gray-700 align-top border border-gray-300 rounded-md" style={{ width: `${100 / 7}%` }}>
                                                                {(wwhEmployeeDataDisplay.slots_by_weekday || []).flatMap((weekDay) => {
                                                                    if (weekDay.week_day - 1 === columnIndex) {
                                                                        const timeSlots = weekDay.working_hour?.time_slots || [];
                                                                        const additionalTimeSlots = weekDay.time_slots || [];
                                                                        const allTimeSlots = [...timeSlots, ...additionalTimeSlots];

                                                                        allTimeSlots.sort((a, b) => {
                                                                            const timeA = new Date(`1970-01-01T${a.start_at}`);
                                                                            const timeB = new Date(`1970-01-01T${b.start_at}`);
                                                                            return timeA.getTime() - timeB.getTime();
                                                                        });

                                                                        return allTimeSlots;
                                                                    }
                                                                    return [];
                                                                }).map((timeSlot, timeSlotIndex) => (
                                                                    <div key={`${columnIndex}-${timeSlotIndex}`} className="mt-2 mb-5">
                                                                        <BriefcaseIcon className='h-5 w-5 inline-block shrink-0 me-1' />
                                                                        <span 
                                                                            className="font-bold underline-offset-4" 
                                                                            style={{
                                                                                textDecoration: 'underline',
                                                                                textDecorationColor: '#2b6cb0',
                                                                                textDecorationThickness: '1px',
                                                                        }}>
                                                                            {timeSlot.start_at} - {timeSlot.end_at}
                                                                        </span>
                                                                        {timeSlot.breaks.map((breakItem, breakIndex) => (
                                                                            <div key={breakIndex} className="my-1 flex items-center gap-1">
                                                                                <span className="text-sm font-medium text-gray-700 flex items-center">
                                                                                    <LocalBarOutlinedIcon style={{ height: '17px', width: '17px', marginRight: '1px' }} />
                                                                                </span>

                                                                                {!breakItem?.is_visible && (
                                                                                    <EyeSlashIcon className="h-4 w-4 text-red-500" strokeWidth={2} />
                                                                                )}

                                                                                <span>{breakItem.start_at} - {breakItem.end_at}</span>
                                                                            </div>
                                                                        ))}
                                                                    </div>
                                                                ))}
                                                            </td>
                                                        ))}
                                                    </tr>
                                                </tbody>
                                            </table>
                                            {(selectedWorkingHour && selectedWorkingHour !== wwhEmployeeData) ? (
                                                <div className='col-span-3 my-4 mx-8 flex justify-center'>
                                                    {(profile.customer_type !== 'employee' || hasManageShiftsPermission) ? (
                                                        <>
                                                            <button 
                                                                className='bg-indigo-600 hover:bg-indigo-500 text-white font-bold py-2 px-4 rounded-md focus:outline-none focus:shadow-outline mr-4 w-24'
                                                                onClick={handleSaveEmployeeWWH}
                                                            >
                                                                Salva
                                                            </button>
                                                            <button 
                                                                className='border block rounded-md bg-grey-400 px-3 py-2 mr-2 text-center text-sm font-semibold text-grey-800 shadow-sm hover:bg-grey-500 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-grey-600 w-24'
                                                                onClick={handleCancelSave}
                                                            >
                                                                Annulla
                                                            </button>
                                                        </>
                                                    ) : (
                                                        <div className='text-center block text-md font-medium text-indigo-600'>
                                                            Non hai i permessi per modificare il turno
                                                        </div>
                                                    )}
                                                </div>
                                            ) : (
                                                <div className='col-span-2 my-4 mx-8 flex justify-center'>
                                                    {(profile.customer_type !== 'employee' || hasManageShiftsPermission) ? (
                                                        <button 
                                                            className='bg-indigo-600 hover:bg-indigo-500 text-white font-bold py-2 px-4 rounded-md focus:outline-none focus:shadow-outline mr-4 w-24'
                                                            onClick={handleDeleteEmployeeWWH}
                                                        >
                                                            Elimina
                                                        </button>
                                                    ) : (
                                                        <div className='text-center block text-md font-medium text-indigo-600'>
                                                            Non hai i permessi per modificare il turno
                                                        </div>
                                                    )}
                                                </div>
                                            )}
                                        </div>
                                    )}
                                </>
                            ) : shiftType === 'senza_calendario' ? (
                                <div className='col-span-5 flex justify-center py-5 items-center w-full rounded-lg border-2 border-dashed border-gray-300 text-sm font-semibold text-gray-900'>
                                    <div className="text-center p-5">
                                        <p>La tipologia di contratto del dipendente è "senza calendario".</p>
                                        <p>Non è possibile assegnare il turno di lavoro</p>
                                    </div>
                                </div>
                            ) : (
                                <div className='col-span-5 flex justify-center py-5 items-center w-full rounded-lg border-2 border-dashed border-gray-300 text-sm font-semibold text-gray-900'> 
                                    <div className="text-center p-5">
                                        <p>Il turno del dipendente può essere assegnato sul calendario</p>
                                        <p>Vai alla pagina di <Link to="/assign_working_hour"><span className='underline text-indigo-700 uppercase cursor-pointer'>assegnazione turno</span></Link></p>
                                    </div>
                                </div>
                            )}
                        </>
                    ) : (
                        <div className='col-span-5 flex justify-center py-5 items-center w-full rounded-lg border-2 border-dashed border-gray-300 text-sm font-semibold text-gray-900'> 
                            <div className="text-center p-5">
                                <p>L'orario di lavoro non è presente sull'attuale contratto attivo e non è possibile assegnare un turno.</p>
                                <p>
                                    Vai alla tab di{' '}
                                    <span
                                        className="underline text-indigo-700 uppercase cursor-pointer"
                                        onClick={() => {
                                            onTabContractChange(2);
                                            window.location.href = `/dipendenti/${codiceFiscale}/edit?tab=2`;
                                        }}
                                    >
                                        modifica contratto
                                    </span>{' '}
                                    per salvare l'orario settimanale.
                                </p>
                            </div>
                        </div>
                    )}
                </div>
            ) : (
                <div className="m-8 rounded-lg border-2 border-dashed border-gray-300 p-6 text-center hover:border-gray-400 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2 text-sm font-semibold text-gray-900">
                    <div className="text-center p-5">
                        <p>Non sono presenti contratti attivi</p>
                    </div>
                </div>
            )}
        </div> 
    );
};

export default TurnoTabEmployee;
